<script>
/**
 * PROGRESSO do cartão flutuante do painel: lista vertical SEM linhas ligando os
 * passos (pedido do usuário, 17/09/2026). Em andamento = só o
 * `v-progress-circular` primary; concluída = `check-circle` primary, que
 * ENTRA animado (a troca spinner → check é uma `<transition>` por status);
 * pendente = círculo vazio `--neutral-300`; falhou = `error-circle` danger.
 * Burro: recebe os passos normalizados (`utils/progresso-do-turno.js`).
 *
 * `maxVisiveis` (opt-in, só o cartão flutuante): janela de etapas a partir da
 * atual (`utils/janela-do-progresso.js`); as concluídas que saem da janela
 * colapsam (fade + altura) e viram a linha "✓ N concluídas". Sem a prop (a
 * bolha do plano) a lista é inteira e o `transition-group` fica sem nome.
 * `compacto` (também só o cartão): marcador 16px, texto 13px, respiro menor.
 */
import { PAINEL_DO_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { STATUS_DO_PASSO } from '@modules/aawz-intelligence-v2/utils/progresso-do-turno'
import { janelaDoProgresso } from '@modules/aawz-intelligence-v2/utils/janela-do-progresso'
import { ICONE_DE_CONCLUIDAS_OCULTAS, ICONE_DE_CONCLUIDO, ICONE_DE_FALHA } from './painel-do-chat.constantes'

const TAMANHO_DO_MARCADOR = 18
const TAMANHO_DO_MARCADOR_COMPACTO = 16
const TAMANHO_DO_ICONE_DAS_OCULTAS = 12
const TRANSICAO_DA_JANELA = 'painel-passo-janela'

export default {
  name: 'IntelligenceV2PainelSecaoProgresso',
  props: {
    // O cartão "Plano" da bolha reusa a lista sem o título da seção.
    semTitulo: {
      type: Boolean,
      default: false,
    },
    passos: {
      type: Array,
      default: () => [],
    },
    // Densidade do cartão flutuante; a bolha do plano segue no tamanho normal.
    compacto: {
      type: Boolean,
      default: false,
    },
    // Máximo de etapas visíveis; `null` = todas.
    /**
     * O TURNO está trabalhando agora?
     *
     * ⚠️ Separado de "a etapa está em andamento" de propósito (achado do
     * usuário na tela, 18/09/2026): uma etapa que espera a RESPOSTA da pessoa
     * fica `em_andamento` o tempo todo, e o spinner ali afirmava trabalho que
     * não existia — no tempo dela, não no nosso. Com `false`, a etapa corrente
     * continua marcada (anel vazado em `primary`), só não gira.
     *
     * Invertida (`turnoParado`, e não `turnoTrabalhando`) porque a regra do
     * repo exige prop booleana com default `false` — e o default tem de
     * preservar o comportamento de antes: quem não passa nada continua
     * girando na etapa corrente.
     */
    turnoParado: {
      type: Boolean,
      default: false,
    },
    maxVisiveis: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      titulo: PAINEL_DO_CHAT.PROGRESSO,
      ICONE_DE_CONCLUIDO,
      ICONE_DE_FALHA,
      ICONE_DE_CONCLUIDAS_OCULTAS,
      TAMANHO_DO_ICONE_DAS_OCULTAS,
    }
  },
  computed: {
    tamanhoDoMarcador() {
      return this.compacto ? TAMANHO_DO_MARCADOR_COMPACTO : TAMANHO_DO_MARCADOR
    },
    janela() {
      return janelaDoProgresso(this.passos, this.maxVisiveis)
    },
    /**
     * ★ [MUST-7 do review do PR #2319] O passo de 13px do cartão flutuante vem
     * da partial do módulo (`design/_ia-texto.sass`), dona única dos tamanhos
     * fora da escala do DS — não de um `font-size` cru neste `<style scoped>`.
     *
     * ⚠️ É TROCA, não soma: `.az-text-primary.az-size-14` tem especificidade
     * (0,2,0) e venceria a classe de passo (0,1,0). No compacto o `az-size-14`
     * sai de cena.
     */
    classeDoPasso() {
      return this.compacto ? 'ia-texto-corpo' : 'az-size-14'
    },
    nomeDaTransicao() {
      return this.maxVisiveis ? TRANSICAO_DA_JANELA : ''
    },
    textoDasOcultas() {
      return PAINEL_DO_CHAT.CONCLUIDAS_OCULTAS(this.janela.concluidasOcultas)
    },
    linhas() {
      // ⚠️ ARROW: o corpo lê `this.turnoParado`. Com `function (passo)` o
      // `this` é `undefined` (módulo ES é strict) e o computed EXPLODE —
      // a lista inteira some da tela sem erro visível no lugar certo.
      return this.janela.visiveis.map((passo) => {
        return {
          id: passo.id,
          titulo: passo.titulo,
          detalhe: passo.detalhe || '',
          rotuloDoStatus: PAINEL_DO_CHAT.STATUS_DO_PASSO[passo.status] || '',
          status: passo.status,
          corrente: passo.status === STATUS_DO_PASSO.EM_ANDAMENTO,
          emAndamento:
            passo.status === STATUS_DO_PASSO.EM_ANDAMENTO &&
            !this.turnoParado,
          concluida: passo.status === STATUS_DO_PASSO.CONCLUIDA,
          pendente: passo.status === STATUS_DO_PASSO.PENDENTE,
          falhou: passo.status === STATUS_DO_PASSO.FALHOU,
          ariaCurrent: passo.status === STATUS_DO_PASSO.EM_ANDAMENTO ? 'step' : null,
        }
      })
    },
  },
}
</script>

<template>
  <section
    class="painel-secao-progresso d-flex flex-column az-gap-small"
    :class="{ 'painel-secao-progresso--compacto': compacto }"
    data-test="painel-secao-progresso"
  >
    <h3 v-if="!semTitulo" class="az-text-primary az-size-14 font-weight-medium ma-0 mb-1">{{ titulo }}</h3>
    <transition :name="nomeDaTransicao">
      <p
        v-if="janela.concluidasOcultas"
        class="painel-secao-progresso__ocultas d-flex align-center az-gap-xxsmall ia-sidebar-aux ma-0"
        data-test="painel-secao-progresso-ocultas"
      >
        <icon-svg :name="ICONE_DE_CONCLUIDAS_OCULTAS" :size="TAMANHO_DO_ICONE_DAS_OCULTAS" color="var(--neutral-600)" />
        {{ textoDasOcultas }}
      </p>
    </transition>
    <!-- `<ol is="transition-group">` em vez de `<transition-group tag="ol">`: é
         o MESMO vnode (o `transition-group` lê o `tag` do `is`), mas o `<li>`
         fica literalmente dentro de um `<ol>` no template (Sonar
         Web:ItemTagNotWithinContainerTagCheck) — mesmo precedente de
         `raciocinio-linha.vue`. -->
    <ol
      is="transition-group"
      :name="nomeDaTransicao"
      class="painel-secao-progresso__lista pa-0 ma-0"
    >
      <li
        v-for="linha in linhas"
        :key="linha.id"
        class="painel-secao-progresso__passo d-flex align-start az-gap-small"
        :aria-current="linha.ariaCurrent"
        data-test="painel-secao-progresso-passo"
      >
        <span
          class="painel-secao-progresso__marcador d-inline-flex align-center justify-center flex-shrink-0"
          role="img"
          :aria-label="linha.rotuloDoStatus"
          :title="linha.rotuloDoStatus"
          data-test="painel-secao-progresso-marcador"
        >
          <transition name="painel-marcador" mode="out-in">
            <v-progress-circular
              v-if="linha.emAndamento"
              :key="linha.status"
              class="painel-secao-progresso__spinner"
              indeterminate
              :size="tamanhoDoMarcador"
              :width="1.5"
              color="primary"
            />
            <icon-svg
              v-else-if="linha.concluida"
              :key="linha.status"
              class="painel-secao-progresso__check"
              :name="ICONE_DE_CONCLUIDO"
              :size="tamanhoDoMarcador"
              color="primary"
            />
            <icon-svg
              v-else-if="linha.falhou"
              :key="linha.status"
              :name="ICONE_DE_FALHA"
              :size="tamanhoDoMarcador"
              color="danger"
            />
            <span
              v-else-if="linha.corrente"
              :key="linha.status"
              class="painel-secao-progresso__corrente"
            />
            <span v-else :key="linha.status" class="painel-secao-progresso__pendente" />
          </transition>
        </span>
        <span
          class="painel-secao-progresso__texto d-flex flex-column"
          :class="[{ 'painel-secao-progresso__texto--pendente': linha.pendente }, classeDoPasso]"
        >
          <span
            class="painel-secao-progresso__titulo az-text-primary"
            :class="classeDoPasso"
            data-test="painel-secao-progresso-titulo"
          >{{
            linha.titulo
          }}</span>
          <span
            v-if="linha.detalhe"
            class="ia-sidebar-aux"
            :class="{ 'painel-secao-progresso__detalhe--falhou': linha.falhou }"
            data-test="painel-secao-progresso-detalhe"
          >{{ linha.detalhe }}</span>
        </span>
      </li>
    </ol>
  </section>
</template>

<style lang="sass" scoped>
$marcador: 18px
// Altura da 1ª linha do título (14px × 1.43): o marcador ocupa essa mesma
// caixa e fica centrado nela — alinhado ao texto mesmo com `detalhe` embaixo.
$linha-do-titulo: 20px
$respiro-entre-passos: 12px
// Teto da altura de um passo na saída (título + detalhe em duas linhas).
$altura-maxima-do-passo: 96px

// Respiro por `margin` (não `gap`): o passo que sai da janela colapsa a
// própria margem junto com a altura, sem deixar um buraco de `gap`.
.painel-secao-progresso__lista
  display: flex
  flex-direction: column
  list-style: none

.painel-secao-progresso__passo:not(:last-child)
  margin-bottom: $respiro-entre-passos

.painel-secao-progresso__marcador
  width: $marcador
  height: $linha-do-titulo

// Mesmo diâmetro visual do `check-circle` e do spinner (o glifo do ícone
// ocupa ~15px dos 18 da caixa) — com 12px a bolinha parecia de outro tamanho.
.painel-secao-progresso__pendente
  box-sizing: border-box
  width: 15px
  height: 15px
  border: 1.5px solid var(--neutral-300)
  border-radius: 50%

// A etapa CORRENTE com o turno parado: o mesmo anel do pendente, na cor da
// marca e mais encorpado. Diz "é aqui que você está" sem dizer "estou
// trabalhando" — que é o que o spinner dizia, e era falso enquanto a etapa
// esperava a resposta da pessoa (achado do usuário, 18/09/2026).
.painel-secao-progresso__corrente
  box-sizing: border-box
  width: 15px
  height: 15px
  border: 2px solid var(--primary)
  border-radius: 50%

.painel-secao-progresso__texto
  min-width: 0
  line-height: $linha-do-titulo

// ⚠️ `.az-size-14` fixa `line-height: 14px`: a caixa do título ficava no topo
// dos 20px e o texto 3px acima do centro do ícone (medido no navegador). O
// título precisa da MESMA linha de 20px do marcador.
.painel-secao-progresso__titulo
  line-height: $linha-do-titulo

// Cartão flutuante: 16px de marcador, texto 13px numa linha de 18px (a mesma
// altura do marcador, como a regra de 20px acima: o marcador segue alinhado à
// 1ª linha e o texto que quebra fica mais junto), 6px até o texto e 4px entre
// etapas (o `az-gap-xsmall`, em margem por causa do colapso da janela).
$marcador-compacto: 16px
$linha-do-titulo-compacto: 18px

.painel-secao-progresso--compacto
  .painel-secao-progresso__passo
    gap: 6px

  .painel-secao-progresso__passo:not(:last-child)
    margin-bottom: 4px

  .painel-secao-progresso__marcador
    width: $marcador-compacto
    height: $linha-do-titulo-compacto

  .painel-secao-progresso__pendente
    width: 13px
    height: 13px

  // O passo de 13px vem de `design/_ia-texto.sass` (classe no template).
  .painel-secao-progresso__texto,
  .painel-secao-progresso__titulo
    line-height: $linha-do-titulo-compacto

// Etapa que ainda não começou: "desabilitada" — cinza, sem competir com a
// que está em andamento.
.painel-secao-progresso__texto--pendente
  opacity: 0.5

// [T024] O MOTIVO de uma etapa que falhou é texto, então leva o tom de TEXTO
// dos status (`_controls.sass`: "tons de texto legível das cores de status"),
// nunca o `--danger` gráfico do ícone ao lado — e nunca hex.
.painel-secao-progresso__detalhe--falhou
  color: var(--danger-text)

// Spinner mais fino (`width` 1.5) e mais DEVAGAR que o padrão do Vuetify
// (giro 1.4s / traço 2s): calmo, não ansioso.
.painel-secao-progresso__spinner
  ::v-deep svg
    animation-duration: 2.4s

  ::v-deep .v-progress-circular__overlay
    animation-duration: 3s

// Spinner → check: o check "estala" (escala com o easing pop da navbar), o
// spinner some em fade. Os outros estados só cruzam em fade.
.painel-marcador-enter-active
  transition: opacity 180ms ease-out, transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1)

.painel-marcador-leave-active
  transition: opacity 120ms ease-in

.painel-marcador-enter,
.painel-marcador-leave-to
  opacity: 0

.painel-marcador-enter.painel-secao-progresso__check
  transform: scale(0.4)

// Janela do cartão: a etapa que sai (e a linha das ocultas) some em fade +
// colapso de altura; a que entra faz o caminho inverso.
.painel-passo-janela-enter-active,
.painel-passo-janela-leave-active
  overflow: hidden
  transition: opacity var(--motion-duration-default) var(--motion-easing-standard), max-height var(--motion-duration-default) var(--motion-easing-standard), margin var(--motion-duration-default) var(--motion-easing-standard)

.painel-passo-janela-enter-to,
.painel-passo-janela-leave
  max-height: $altura-maxima-do-passo

.painel-passo-janela-enter,
.painel-passo-janela-leave-to
  opacity: 0
  max-height: 0

// Especificidade do `:not(:last-child)` da margem: o passo precisa da classe junto.
.painel-secao-progresso__passo.painel-passo-janela-enter,
.painel-secao-progresso__passo.painel-passo-janela-leave-to,
.painel-secao-progresso--compacto .painel-secao-progresso__passo.painel-passo-janela-enter,
.painel-secao-progresso--compacto .painel-secao-progresso__passo.painel-passo-janela-leave-to
  margin-bottom: 0

@media (prefers-reduced-motion: reduce)
  .painel-marcador-enter-active,
  .painel-marcador-leave-active,
  .painel-passo-janela-enter-active,
  .painel-passo-janela-leave-active
    transition: none
</style>
